import { useCallback, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import type { LogoMeta } from "@/api/types";
import { cn } from "@/lib/cn";
import { AlertIcon, CheckIcon, TrashIcon, UploadIcon } from "./icons";

type Props = {
  value: LogoMeta;
  onUpload: (logo: LogoMeta, error?: string) => void;
  onRemove: () => void;
};

/**
 * Logo dropzone with file validation + soft warnings.
 *
 * Hard fail (rejects): file type ∉ {PNG, JPG, SVG}, size > 8 MB.
 * Soft warn: < 300 DPI at print size, no transparency, unusual aspect.
 *
 * The DPI estimate assumes the logo prints at 32 mm wide (kit box spec).
 * In Phase 3 the spec becomes per-kit (some items will use larger logo
 * areas) and the dropzone reads the constraint from the kit definition.
 */
export function LogoDropzone({ value, onUpload, onRemove }: Props) {
  const [busy, setBusy] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback(
    (file: File | undefined) => {
      if (!file) return;
      setBusy(true);

      const okType = ["image/png", "image/svg+xml", "image/jpeg"].includes(file.type);
      if (!okType) {
        onUpload(null, "Unsupported file type. Use PNG, JPG or SVG.");
        setBusy(false);
        return;
      }
      if (file.size > 8 * 1024 * 1024) {
        onUpload(null, "File over 8 MB. Compress and retry.");
        setBusy(false);
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const src = String(e.target?.result ?? "");
        if (file.type === "image/svg+xml") {
          onUpload({
            src, name: file.name, size: file.size, type: file.type,
            width: null, height: null, dpi: null, hasAlpha: true, vector: true,
          });
          setBusy(false);
          return;
        }
        const img = new Image();
        img.onload = () => {
          let hasAlpha = false;
          try {
            const c = document.createElement("canvas");
            const w = (c.width = Math.min(img.width, 200));
            const h = (c.height = Math.min(img.height, 200));
            const ctx = c.getContext("2d");
            if (ctx) {
              ctx.drawImage(img, 0, 0, w, h);
              const data = ctx.getImageData(0, 0, w, h).data;
              for (let i = 3; i < data.length; i += 4 * 16) {
                if (data[i]! < 250) { hasAlpha = true; break; }
              }
            }
          } catch { /* CORS — assume opaque */ }
          // 32 mm print-width assumption; px / 32 * 25.4 = px per inch
          const dpi = Math.round((img.width / 32) * 25.4);
          onUpload({
            src, name: file.name, size: file.size, type: file.type,
            width: img.width, height: img.height, dpi, hasAlpha, vector: false,
          });
          setBusy(false);
        };
        img.src = src;
      };
      reader.readAsDataURL(file);
    },
    [onUpload],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (files) => handleFile(files[0]),
    accept: { "image/png": [".png"], "image/jpeg": [".jpg", ".jpeg"], "image/svg+xml": [".svg"] },
    multiple: false,
    noClick: true,
  });

  // With-file state
  if (value && value.src) {
    const checks: { lvl: "ok" | "warn" | "bad"; text: string }[] = [];
    if (value.vector) {
      checks.push({ lvl: "ok", text: "Vector format — scales cleanly" });
      checks.push({ lvl: "ok", text: "Transparent background" });
    } else {
      if (value.dpi != null) {
        if (value.dpi >= 300)      checks.push({ lvl: "ok",   text: `${value.dpi} DPI at print size` });
        else if (value.dpi >= 200) checks.push({ lvl: "warn", text: `${value.dpi} DPI — below 300 recommended for print` });
        else                       checks.push({ lvl: "bad",  text: `${value.dpi} DPI — too low, may print blurred` });
      }
      checks.push(
        value.hasAlpha
          ? { lvl: "ok",   text: "Transparent background" }
          : { lvl: "warn", text: "No transparency — white square may show on box" },
      );
    }
    if (value.width && value.height) {
      const ratio = value.width / value.height;
      if (ratio < 0.5 || ratio > 6)
        checks.push({ lvl: "warn", text: `Unusual aspect ratio (${ratio.toFixed(1)}:1)` });
    }

    return (
      <div className="flex gap-3 rounded-lg border border-[var(--border-1)] bg-white p-3">
        <div className="grid h-16 w-16 flex-shrink-0 place-items-center overflow-hidden rounded-md border border-[var(--border-1)] bg-white p-1">
          <img src={value.src} alt="Uploaded logo" className="max-h-full max-w-full object-contain" />
        </div>
        <div className="min-w-0 flex-1 text-xs">
          <div className="truncate font-semibold text-fg-1">{value.name}</div>
          <div className="mt-0.5 font-mono text-fg-3">
            {value.vector ? "SVG · vector" : `${value.width}×${value.height} px · ${(value.size / 1024).toFixed(0)} KB`}
          </div>
          <div className="mt-1.5 flex flex-col gap-0.5">
            {checks.map((c, i) => (
              <div
                key={i}
                className={cn(
                  "flex items-center gap-1.5 text-xxs",
                  c.lvl === "ok"   && "text-[color:var(--ok)]",
                  c.lvl === "warn" && "text-[#6B4200]",
                  c.lvl === "bad"  && "text-[color:var(--danger)]",
                )}
              >
                {c.lvl === "ok"
                  ? <CheckIcon width={11} height={11} strokeWidth={2.5} />
                  : <AlertIcon width={11} height={11} />}
                <span>{c.text}</span>
              </div>
            ))}
          </div>
        </div>
        <button
          type="button"
          onClick={onRemove}
          className="grid h-7 w-7 self-start place-items-center rounded text-fg-3 hover:bg-bg-2 hover:text-fg-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-focus)]"
          aria-label="Remove logo"
          title="Remove logo"
        >
          <TrashIcon width={14} height={14} />
        </button>
      </div>
    );
  }

  // Empty state
  return (
    <div
      {...getRootProps()}
      onClick={() => fileInputRef.current?.click()}
      className={cn(
        "flex cursor-pointer flex-col items-center gap-1.5 rounded-lg border-2 border-dashed border-[var(--border-2)] bg-bg-1 px-4 py-6 text-center transition-colors",
        "hover:border-[var(--studio)] hover:bg-[var(--studio-wash)]",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-focus)]",
        isDragActive && "border-[var(--studio)] bg-[var(--studio-wash)]",
      )}
      role="button"
      tabIndex={0}
    >
      <input {...getInputProps()} ref={fileInputRef} />
      <UploadIcon width={28} height={28} className="text-[color:var(--studio)]" />
      <div className="text-sm font-semibold text-fg-1">
        {busy ? "Processing…" : "Drop your logo here, or click to browse"}
      </div>
      <div className="text-xxs text-fg-3">
        SVG preferred · PNG or JPG accepted · 8 MB max · 300 DPI recommended
      </div>
    </div>
  );
}
